import React, {useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {AttendanceEmployeeSummary} from '@/entities/attendance/types';
import {PersonAvatar} from '@/entities/personnel/ui/PersonAvatar';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {formatCount, formatHoursTotal, formatIsoDate} from '@/shared/lib/format';
import {Explain, type Definition} from '@/shared/ui/explain';
import {FilterSelect} from '@/shared/ui/filter-bar';
import {PressCard} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {AppText} from '@/shared/ui/text';
import {Cell, type Column} from '@/shared/ui/table';
import {DataList} from '@/shared/ui/data-list';
import {radii, spacing, useTheme, type Palette} from '@/shared/ui/theme';
import {
  attendanceRate,
  employeeAccessors,
  employeeTiebreak,
  filterByIssue,
  filterEmployees,
  formatRate,
  type IssueFilter,
} from '../lib/aggregate';
import {ALARMING_ATTENDANCE_RATE} from '../lib/narrative';

/**
 * Everybody in the scope, one row each, sortable by every column that is a number.
 *
 * This is the working surface of the whole report: "who was absent most", "who is
 * chronically five minutes late", "who worked the most hours" are all the same table
 * ordered three different ways, which is why the sorting lives on the headers rather
 * than behind a dropdown of pre-canned questions.
 *
 * Two form factors, one component. On a desktop window it is a real table — forty
 * people at once, columns lined up so a number can be compared down the page. On a
 * phone the same rows are cards, because a nine-column table at 390pt is a horizontal
 * scroll, and a horizontal scroll is a lost user (the argument shared/ui/table.tsx
 * makes at length). The card carries the four figures worth carrying; the rest is one
 * tap away on the person's own report.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  pager: {paddingTop: spacing(1)},
  /* Переносится: поиск, отбор и «?» втроём в 390pt телефона не помещаются, а
     фиксированная строка их бы сплющила. Поле поиска держит свою минимальную ширину и
     уезжает на первую строку одно. */
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(3),
  },
  toolbarField: {flex: 1, minWidth: 200},
  cards: {gap: spacing(2)},
  nameCell: {flexDirection: 'row', alignItems: 'center', gap: spacing(3), flex: 1, minWidth: 0},
  nameCellBody: {flex: 1, minWidth: 0},
  card: {gap: spacing(2)},
  cardHead: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  cardWho: {flex: 1, gap: 1},
  figures: {flexDirection: 'row', gap: spacing(2)},
  figure: {
    flex: 1,
    borderRadius: radii.sm,
    paddingVertical: spacing(2),
    paddingHorizontal: spacing(2),
    gap: 1,
  },
  sortRow: {flexDirection: 'row', gap: spacing(2), paddingRight: spacing(4)},
  sortChip: {
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(1) + 2,
    borderRadius: radii.md,
    borderWidth: 1,
  },
});

/** The orders worth offering on a phone, where chips compete with the cards for room. */
/** Порядок по умолчанию. Ссылка модульная: новая на каждый рендер пересортировала бы
    список впустую. */
const INITIAL_SORT = {key: 'name', direction: 'asc'} as const;

const PHONE_SORTS: readonly string[] = [
  'name',
  'absent',
  'late',
  'worked',
  'rate',
];

/**
 * Каким цветом печатать явку человека.
 *
 * Раньше — всегда фирменным зелёным, и «0%» выглядело достижением: на карточке
 * человека, не появившегося ни разу, стояла зелёная цифра. Цвет обязан значить то же,
 * что и везде в кабинете, иначе он не значит ничего.
 *
 * Порог — тот же, по которому краснеет вердикт всего отчёта (`ALARMING_ATTENDANCE_RATE`):
 * одна мера для человека и для компании, иначе строка в таблице и заголовок над ней
 * расходятся в оценке одних и тех же данных.
 */
function rateTone(rate: number | null, p: Palette): string {
  if (rate == null) {
    return p.mutedForeground;
  }
  if (rate >= 1) {
    return p.signalInk;
  }
  return rate < ALARMING_ATTENDANCE_RATE ? p.destructiveInk : p.foreground;
}

/** One number on a phone card, tinted only when it is bad news. */
function Figure({
  value,
  label,
  fg,
  bg,
}: {
  value: string;
  label: string;
  fg: string;
  bg: string;
}) {
  return (
    <View style={[styles.figure, {backgroundColor: bg}]}>
      <AppText variant="mono" color={fg}>
        {value}
      </AppText>
      <AppText variant="micro" color={fg} numberOfLines={1}>
        {label}
      </AppText>
    </View>
  );
}

export function EmployeeReportList({
  employees,
  onOpen,
  companyId,
  selectedRecordId,
  /**
   * A short, pre-picked list in a narrow column — «Требуют внимания» in the desktop
   * sidebar. The breakpoint says "desktop" and would draw the full table, but the
   * column is 300pt wide: nine columns of fixed widths do not fit into a third of the
   * window just because the window is wide. Cards do, and five rows need neither a
   * search field nor a sort control.
   */
  compact = false,
  /**
   * Отбор по виду замечания — снаружи, если экран им управляет.
   *
   * На «Посещаемости» им управляют плитки наверху: нажатие на «Опозданий» показывает
   * в таблице ровно опоздавших, и это же значение уезжает в адрес. Не передан —
   * список ведёт отбор сам, как и раньше; так он и живёт в отчёте по отделу.
   */
  issue: issueProp,
  onIssueChange,
}: {
  employees: AttendanceEmployeeSummary[];
  onOpen: (row: AttendanceEmployeeSummary) => void;
  /** Whose faces to fetch. Omitted — initials, which is what a test host passes. */
  companyId?: string;
  selectedRecordId?: string | null;
  compact?: boolean;
  issue?: IssueFilter;
  onIssueChange?: (next: IssueFilter) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const {isDesktop} = useBreakpoint();

  const [search, setSearch] = useState('');
  /**
   * Кого показывать: всех или тех, у кого за период есть замечание одного вида.
   *
   * Это ответ на вопрос, который к отчёту задают чаще прочих и которого он до сих пор
   * не давал: «кто опаздывал». Столбец «Опозданий» отвечал на него сортировкой —
   * человек нажимал заголовок и читал сверху вниз, пока не кончались ненулевые. Список
   * ровно из опоздавших короче и не требует решать, где кончается «опаздывал».
   *
   * Виды не смешиваются в один «есть замечания»: пропуск, опоздание и ранний уход —
   * три разговора с тремя разными людьми, и объединять их в одну кнопку значит вернуть
   * читателя к чтению столбцов.
   */
  const [ownIssue, setOwnIssue] = useState<IssueFilter>('');
  const issue = issueProp ?? ownIssue;
  const setIssue = onIssueChange ?? setOwnIssue;
  const matched = useMemo(
    () => filterByIssue(filterEmployees(employees, search), issue),
    [employees, search, issue],
  );

  // Порядок — по общим правилам кабинета; отчёт задаёт только то, что лежит в
  // столбцах, и чем разбивать равенство (см. lib/aggregate).
  const accessors = useMemo(() => employeeAccessors(), []);
  const hour = t('attendance.units.hour');
  const minute = t('attendance.units.minute');

  const columns: Column<AttendanceEmployeeSummary>[] = [
    {
      key: 'name',
      sortKey: 'name',
      title: t('reports.col.employee'),
      // 2 → 3: лицо съедает 28pt плюс отступ из ширины этой колонки, и при восьми
      // столбцах на 1180pt фамилии начали обрезаться до «Алиш…». Колонка, в которой
      // не помещается имя, хуже, чем колонка без лица.
      flex: 3,
      // Не `NameCell`: та кладёт содержимое ВНУТРЬ <Text>, а здесь две строки и лицо.
      render: row => (
        <View style={styles.nameCell}>
          {companyId ? (
            <PersonAvatar
              companyId={companyId}
              recordId={row.record_id}
              name={row.full_name}
              size={28}
            />
          ) : null}
          <View style={styles.nameCellBody}>
            <Cell>{row.full_name}</Cell>
            {formatIsoDate(row.detached_at) ? (
              <AppText variant="micro" numberOfLines={1} color={p.mutedForeground}>
                {t('reports.detachedOn', {date: formatIsoDate(row.detached_at)})}
              </AppText>
            ) : row.position ? (
              <AppText variant="micro" numberOfLines={1}>
                {row.position}
              </AppText>
            ) : null}
          </View>
        </View>
      ),
    },
    {
      key: 'number',
      title: t('reports.col.number'),
      width: 84,
      // 1180 → 1360. Восемь колонок фиксированной ширины оставляли имени 92pt на
      // окне 1180 — «Алиш…» с фотографией и «Алишер К…» без неё. Табельный и
      // переработки и раньше были назначены первыми на вылет; теперь они улетают
      // там, где иначе не помещается то, ради чего в таблицу и смотрят, — фамилия.
      minWidth: 1360,
      render: row => (
        <Cell mono tone={p.mutedForeground}>
          {row.employee_number || '—'}
        </Cell>
      ),
    },
    {
      key: 'present',
      sortKey: 'present',
      title: t('reports.col.present'),
      width: 84,
      align: 'right',
      render: row => <Cell mono>{formatCount(row.summary.present_days)}</Cell>,
    },
    {
      key: 'absent',
      sortKey: 'absent',
      title: t('reports.col.absent'),
      width: 84,
      align: 'right',
      render: row => (
        <Cell mono tone={row.summary.absent_days > 0 ? p.destructiveInk : undefined}>
          {formatCount(row.summary.absent_days)}
        </Cell>
      ),
    },
    {
      key: 'late',
      sortKey: 'late',
      title: t('reports.col.late'),
      width: 84,
      align: 'right',
      render: row => (
        <Cell mono tone={row.summary.late_days > 0 ? p.warningInk : undefined}>
          {formatCount(row.summary.late_days)}
        </Cell>
      ),
    },
    {
      key: 'overtime',
      sortKey: 'overtime',
      title: t('reports.col.overtime'),
      width: 92,
      align: 'right',
      minWidth: 1360,
      render: row => (
        <Cell mono tone={row.summary.overtime_days > 0 ? p.warningInk : undefined}>
          {formatCount(row.summary.overtime_days)}
        </Cell>
      ),
    },
    {
      key: 'worked',
      sortKey: 'worked',
      title: t('reports.col.worked'),
      width: 112,
      align: 'right',
      render: row => (
        <Cell mono>
          {formatHoursTotal(row.summary.total_worked_minutes, hour, minute)}
        </Cell>
      ),
    },
    {
      key: 'rate',
      sortKey: 'rate',
      title: t('reports.col.rate'),
      width: 76,
      align: 'right',
      render: row => (
        <Cell mono tone={rateTone(attendanceRate(row.summary), p)}>
          {formatRate(attendanceRate(row.summary))}
        </Cell>
      ),
    },
  ];

  /** The same glossary the summary board carries, narrowed to the columns here. */
  const definitions: Definition[] = [
    'present',
    'absent',
    'late',
    'overtime',
    'worked',
    'attendance',
  ].map(id => ({
    term: t(`reports.def.${id}.term`),
    meaning: t(`reports.def.${id}.meaning`),
    formula: t(`reports.def.${id}.formula`, {defaultValue: ''}) || undefined,
  }));

  return (
    <DataList
      testID="report-employees"
      // Отобранные строки — вход; `total` отличает «сотрудников нет» от «поиск ничего
      // не нашёл»: это два разных сообщения и два разных следующих действия.
      rows={matched}
      total={employees.length}
      columns={columns}
      keyOf={row => row.record_id}
      accessors={accessors}
      initialSort={INITIAL_SORT}
      tiebreak={employeeTiebreak}
      onRowPress={onOpen}
      selectedKey={selectedRecordId ?? null}
      compact={compact}
      phoneSorts={PHONE_SORTS}
      sortLabel={key => t(`reports.col.${key}`)}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: t('reports.searchEmployees'),
      }}
      filters={
        <FilterSelect
          label={t('reports.issueFilter')}
          value={issue}
          options={[
            {value: '', label: t('actions.any')},
            {value: 'late', label: t('reports.issueLate')},
            {value: 'absent', label: t('reports.issueAbsent')},
            {value: 'earlyLeave', label: t('reports.issueEarlyLeave')},
            {value: 'silent', label: t('reports.issueSilent')},
            {value: 'clean', label: t('reports.issueClean')},
          ]}
          onChange={next => setIssue(next as IssueFilter)}
          phone={!isDesktop}
          testID="report-issue-filter"
        />
      }
      aside={
        <Explain
          testID="explain-columns"
          title={t('reports.glossary')}
          subtitle={t('reports.glossaryColumns')}
          definitions={definitions}
          note={t('reports.glossaryNote')}
        />
      }
      hint={{table: t('reports.tableHint'), cards: t('reports.cardsHint')}}
      empty={<EmptyState title={t('reports.noEmployees')} />}
      noMatches={<EmptyState title={t('reports.noMatches')} />}
      renderCard={row => {
        const s = row.summary;
        return (
          <PressCard
            padding={3}
            style={styles.card}
            onPress={() => onOpen(row)}
            testID={`report-employee-${row.record_id}`}>
            <View style={styles.cardHead}>
              {companyId ? (
                <PersonAvatar
                  companyId={companyId}
                  recordId={row.record_id}
                  name={row.full_name}
                  size={40}
                />
              ) : null}
              <View style={styles.cardWho}>
                <AppText variant="subtitle" numberOfLines={1}>
                  {row.full_name}
                </AppText>
                <AppText variant="caption" numberOfLines={1}>
                  {formatIsoDate(row.detached_at)
                    ? t('reports.detachedOn', {date: formatIsoDate(row.detached_at)})
                    : row.position || t('reports.noPosition')}
                </AppText>
              </View>
              <AppText variant="monoLg" color={rateTone(attendanceRate(s), p)}>
                {formatRate(attendanceRate(s))}
              </AppText>
            </View>
            <View style={styles.figures}>
              <Figure
                value={formatCount(s.present_days)}
                label={t('reports.col.present')}
                fg={p.foreground}
                bg={p.muted}
              />
              <Figure
                value={formatCount(s.absent_days)}
                label={t('reports.col.absent')}
                fg={s.absent_days > 0 ? p.destructiveInk : p.mutedForeground}
                bg={s.absent_days > 0 ? p.destructiveSoft : p.muted}
              />
              <Figure
                value={formatCount(s.late_days)}
                label={t('reports.col.late')}
                fg={s.late_days > 0 ? p.warningInk : p.mutedForeground}
                bg={s.late_days > 0 ? p.warningSoft : p.muted}
              />
              <Figure
                value={formatHoursTotal(s.total_worked_minutes, hour, minute)}
                label={t('reports.col.worked')}
                fg={p.foreground}
                bg={p.muted}
              />
            </View>
          </PressCard>
        );
      }}
    />
  );
}
